import { extend, isGuid, isArray } from '../../util/jquery.js';
import Security from './security.js';

import { hhmmss } from '../../util/date-time';

import createError from '../../util/create-error';

export default class SecurityManager {
    constructor(option) {
        this.apiManager = option.apiManager;
        this.securities = new Map();
        this.quotes = new Map();
        this.listeners = new Map();
        this.extraSecurityInfo = new Map();
    }
    async createByFormData(formData) {
        const res = await this.apiManager.securitiesByFormData({
            formData
        });
        const ret = [];
        res.forEach(securityJSON => {
            if (!this.securities.has(securityJSON.queryKey)) {
                const instrument = `${securityJSON.rtExchangeId}.${securityJSON.rtType}.${securityJSON.rtSymbol}`;
                const extraSecurityInfo =
                    this._getExtraSecurityInfoByInstrument(instrument) || {};
                const security = new Security(
                    extend({}, extraSecurityInfo, securityJSON)
                );
                if (security.isValid()) {
                    this.securities.set(security.queryKey, security);
                }
                ret.push(security);
            } else {
                ret.push(this.securities.get(securityJSON.queryKey));
            }
        });
        return ret;
    }
    async create(symbols, skipIdService = {}) {
        let symbolArray =
            typeof symbols === 'string' ? symbols.split(',') : symbols;
        // remove empty string passing
        symbolArray = symbolArray.filter(symbol => symbol !== '');
        // check if the symbol is options or forex with realtime id format,
        // then create security instance on local
        const { localList, remoteList } = this._extractQuerySymbols(
            symbolArray,
            skipIdService
        );
        localList.forEach(rt => {
            let security, instrument;
            if (isArray(rt)) {
                instrument = rt.join('.');
                const extraSecurityInfo = this._getExtraSecurityInfoByInstrument(
                    instrument
                );
                security = new Security(
                    extend({}, extraSecurityInfo, {
                        rtType: rt[1],
                        rtExchangeId: rt[0],
                        rtMarketDataExchangeID: rt[0],
                        rtSymbol: rt[2],
                        queryKey: instrument,
                        securityId: '',
                        performanceId: '',
                        isRequestIdService: false
                    })
                );
            } else if (isGuid(rt)) {
                instrument = rt;
                security = new Security({
                    rtType: '',
                    rtExchangeId: '',
                    rtMarketDataExchangeID: '',
                    rtSymbol: '',
                    queryKey: instrument,
                    securityId: instrument,
                    performanceId: '',
                    isRequestIdService: false,
                    isGuidSymbol: true
                });
            }

            this.securities.set(instrument, security);
        });
        if (remoteList.length) {
            const securities = await this.apiManager.lookupSecurities(
                remoteList
            );
            securities.forEach(securityJSON => {
                if (
                    securityJSON.queryKey &&
                    !securityJSON._noData &&
                    !this.securities.has(securityJSON.queryKey)
                ) {
                    const instrument = `${securityJSON.rtExchangeId}.${securityJSON.rtType}.${securityJSON.rtSymbol}`;
                    const extraSecurityInfo = this._getExtraSecurityInfoByInstrument(
                        instrument
                    );
                    const security = new Security(
                        extend({}, extraSecurityInfo, securityJSON, {
                            isRequestIdService: true
                        })
                    );
                    this.securities.set(security.queryKey, security);
                }
            });
        }
        return symbolArray.map(queryKey => {
            if (this.securities.has(queryKey)) {
                return this.securities.get(queryKey);
            } else {
                return new Security({
                    rtType: '',
                    rtExchangeId: '',
                    rtMarketDataExchangeID: '',
                    rtSymbol: '',
                    queryKey: queryKey,
                    securityId: '',
                    performanceId: ''
                });
            }
        });
    }
    setExtraSecurityInfo(data) {
        for (const key in data) {
            this.extraSecurityInfo.set(key, data[key]);
        }
    }
    getQuoteInstruments(securities, field, value) {
        if (arguments.length === 3) {
            return securities
                .filter(security => security[field] === value)
                .map(security => security.quoteInstrument);
        } else {
            return securities.map(security => security.quoteInstrument);
        }
    }
    getQuoteInstrumentsWithZeroWatchCount(securities) {
        return securities
            .filter(security => security.watchCount() === 0)
            .map(security => security.quoteInstrument);
    }
    setQuotes(dataType, quote) {
        const quotes = this._getQuotes(dataType, quote.instrument);
        const securities = this.getSecuritiesByField(
            'quoteInstrument',
            quote.instrument
        );
        const updates = [];
        quote.fields.forEach(field => {
            const oldValue = quotes.get(field.name);
            if (oldValue !== field.value) {
                updates.push({
                    name: field.name,
                    oldValue: oldValue,
                    value: field.value
                });
            }
            quotes.set(field.name, field.value);
        });
        if (updates.length) {
            securities.forEach(security => {
                this._getListeners(dataType, security.queryKey).forEach(
                    listener => {
                        listener(security.queryKey, updates);
                    }
                );
            });
        }
    }
    watch(dataType, securities, listener = () => {}) {
        securities.forEach(security => {
            security.watchCount(security.watchCount() + 1);
            const quotes = this._getQuotes(dataType, security.quoteInstrument);
            const updates = [];
            for (const name of Array.from(quotes.keys())) {
                updates.push({
                    name: name,
                    oldValue: undefined,
                    value: quotes.get(name)
                });
            }
            if (updates.length) {
                listener(security.queryKey, updates);
            }
            const listeners = this._getListeners(dataType, security.queryKey);
            listeners.add(listener);
        });
    }
    unwatch(dataType, securities, listener) {
        securities.forEach(security => {
            const _watchCount = security.watchCount();
            if (_watchCount > 0) {
                security.watchCount(_watchCount - 1);
                const listeners = this._getListeners(
                    dataType,
                    security.queryKey
                );
                listeners.delete(listener);
            }
        });
    }
    getSecuritiesByField(field, value) {
        const ret = [];
        for (const security of Array.from(this.securities.values())) {
            if (security[field] === value) {
                ret.push(security);
            }
        }
        return ret;
    }
    getSecurityByMultiFiled(fieldArray, value) {
        for (const security of Array.from(this.securities.values())) {
            const filter = fieldArray.some(field => {
                return security[field] === value;
            });
            if (filter) {
                return security;
            }
        }
        return null;
    }
    getSecuritiesFields(security) {
        return {
            queryKey: security.queryKey,
            marketCloseTime: security.marketCloseTime,
            marketOpenTime: security.marketOpenTime,
            marketPreOpenTime: security.marketPreOpenTime,
            marketPostTradingCloseTime: security.marketPostTradingCloseTime,
            marketDelayedMinutes: security.marketDelayedMinutes,
            estOffsetMinutes: security.estOffsetMinutes,
            gmtOffsetMinutes: security.gmtOffsetMinutes,
            timezone: security.timezone,
            timezoneAbbreviation: security.timezoneAbbreviation
        };
    }
    getExtraExchangeInfo(exchange) {
        let extra;
        if (this.extraSecurityInfo.has(exchange)) {
            const sec = this.extraSecurityInfo.get(exchange);
            extra = {
                MICCode: sec.micCode,
                exchangeName: sec.name,
                shortExchangeName: sec.shortName
            };
        }
        return extra;
    }
    getSecurities(queryKeys) {
        const ret = [];
        queryKeys.forEach(queryKey => {
            if (this.securities.has(queryKey)) {
                ret.push(this.securities.get(queryKey));
            }
        });
        return ret;
    }
    getInvalidSecuritiesError(securities) {
        return createError(`Invalid securities ${securities}`, null, -2);
    }
    _getQuotes(dataType, instrument) {
        if (!this.quotes.has(dataType)) {
            this.quotes.set(dataType, new Map());
        }
        const quotes = this.quotes.get(dataType);
        if (!quotes.has(instrument)) {
            quotes.set(instrument, new Map());
        }
        return quotes.get(instrument);
    }
    _getListeners(dataType, queryKey) {
        if (!this.listeners.has(dataType)) {
            this.listeners.set(dataType, new Map());
        }
        const listeners = this.listeners.get(dataType);
        if (!listeners.has(queryKey)) {
            listeners.set(queryKey, new Set());
        }
        return listeners.get(queryKey);
    }
    _getExtraSecurityInfoByInstrument(instrument) {
        const [exchange, type] = instrument.split('.');
        let extra;
        if (this.extraSecurityInfo.has(exchange)) {
            const sec = this.extraSecurityInfo.get(exchange);
            extra = {
                timezone: sec.timezone,
                timezoneAbbreviation: sec.abbreviation,
                estOffsetMinutes: sec.estOffsetByMinutes,
                gmtOffsetMinutes: sec.gmtOffsetByMinutes,
                marketDelayedMinutes: sec.delayMinute,
                exchangeShortName: sec.shortName
            };
            const marketInfo =
                sec.marketsInfo && sec.marketsInfo[type]
                    ? sec.marketsInfo[type]
                    : null;
            if (!marketInfo) {
                return extra;
            }
            extend(extra, {
                marketCloseTime: hhmmss(marketInfo.closeMinute * 60),
                marketOpenTime: hhmmss(marketInfo.openMinute * 60),
                marketPreOpenTime: hhmmss(
                    (marketInfo.preOpenMinute || marketInfo.openMinute) * 60
                ),
                marketPostTradingCloseTime: hhmmss(
                    (marketInfo.afterHourMinute || marketInfo.closeMinute) * 60
                )
            });
        }
        return extra;
    }
    _extractQuerySymbols(symbolArray, skipIdService) {
        //skipIdService is judge ticker whether skip idService
        const localList = [];
        const remoteList = [];
        symbolArray.forEach(symbol => {
            let security = this.securities.get(symbol);
            if (!security) {
                security = this.getSecurityByMultiFiled(
                    [
                        'quoteInstrument',
                        'securityId',
                        'performanceId',
                        'MICMstarSymbol',
                        'MICRTSymbol'
                    ],
                    symbol
                );
                if (security && security.isRequestIdService) {
                    const _security = security.clone();
                    _security.queryKey = symbol;
                    this.securities.set(symbol, _security);
                }
            }

            if (!security || !security.isRequestIdService) {
                if (/^(\d+?)\.(\d+?)\.(\S+?)$/.test(symbol)) {
                    const temp = symbol.split('.');
                    // to filter options component option ticker request idService.
                    if (skipIdService[temp[1]]) {
                        localList.push(temp);
                    } else {
                        remoteList.push(symbol);
                    }
                } else if (isGuid(symbol)) {
                    localList.push(symbol);
                } else {
                    remoteList.push(symbol);
                }
            }
        });
        return {
            localList,
            remoteList
        };
    }
}
